import React, { useEffect, useRef, useState } from 'react';
import { X, Download, Search, ChevronLeft, ChevronRight, Upload, ArrowLeft, Inbox } from 'lucide-react';
import { API } from '../Common/Api';
import { ErrorToast, SuccessToast } from '../Utils/toast';
import { pageSizeOptions } from '../Utils/pagination';
import CustomSelectInput from './SelectInput';
import ConfirmDialog from '../Common/ConfirmDialog';
import uploading from '../assets/uploading.png';

// Reverse (undo) inventory — role-aware. Opens straight onto the reversible
// list; "Reverse with File" is an action off that list, since either route ends
// in the same manual reversal. Each role may only reverse the tier directly
// below it (enforced on the backend).
const ReverseInventoryModal = ({ open, onClose, onSuccess, role = 'admin' }) => {
  const [activeTab, setActiveTab] = useState('manual'); // 'manual' (default) | 'file'

  // ── File tab state ──
  const [undoFile, setUndoFile] = useState(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const fileInputRef = useRef(null);

  // ── Manual tab state ──
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Admin: searched Tag Ref IDs missing from the list + where each sits now.
  const [notFoundTags, setNotFoundTags] = useState([]);
  // Name filter for the tier below this user — typed value vs. submitted one.
  const [childInput, setChildInput] = useState('');
  const [childName, setChildName] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [selected, setSelected] = useState({}); // { _id: rowMeta }
  const [submitting, setSubmitting] = useState(false);
  // Styled confirm gate: { title, message, onYes } — null when hidden.
  const [confirmState, setConfirmState] = useState(null);
  const requestIdRef = useRef(0);

  // ── Tier walk (admin only) ──
  // Admin holds the whole tree, so it picks which tier's holdings to reverse:
  // a distributor's, a dealer's, or an agent's. Every other role owns exactly
  // one tier below it and skips this entirely.
  const isAdmin = role === 'admin';
  const [tier, setTier] = useState('distributor'); // 'distributor' | 'dealer' | 'agent'
  const [distributors, setDistributors] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [pickedDistributor, setPickedDistributor] = useState('');
  // Dealer and agent are multi-select — several dealers (or agents) can be
  // listed together, so one batch of tags can be reversed across them in a
  // single pass. Held as id arrays; the backend takes a comma-separated list.
  const [pickedDealers, setPickedDealers] = useState([]);
  const [pickedAgents, setPickedAgents] = useState([]);

  const asList = (res) => (Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []));

  // Effect deps can't watch an array by identity — a fresh array each render
  // would refetch forever. The joined ids give a stable primitive instead.
  const dealerIdsParam = pickedDealers.join(',');
  const agentIdsParam = pickedAgents.join(',');

  // The dealer tier needs at least one dealer chosen; the agent tier needs at
  // least one agent. Until then there is nothing meaningful to list, so the
  // fetch is held back.
  const tierReady =
    !isAdmin || tier === 'distributor' ||
    // Dealer tab lists straight away: no pick → every dealer's stock, a
    // distributor → its dealers', dealer(s) → just those (backend scopes it).
    tier === 'dealer' ||
    // Agent tab lists once a distributor is chosen: distributor → all its
    // agents' stock, + dealer → that dealer's agents', agent(s) → just those.
    (tier === 'agent' && (!!pickedDistributor || pickedDealers.length > 0 || pickedAgents.length > 0));

  // "Reverse with File" is offered on every tab (all tiers) for admin, as well as
  // for the other roles. The uploaded file's Reverse_Action column drives what is
  // reversed, independent of which manual tier tab is currently shown.
  const fileRouteAvailable = true;

  // The name box searches the tier below the caller. Once a dealer or agent has
  // been picked from the dropdowns that tier is already pinned exactly, so the
  // box has nothing left to narrow.
  const childSearchAvailable = !isAdmin || tier === 'distributor';

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // Role-based sample files (destination-tier pre-filled).
  const sampleFilesByRole = {
    // Admin can reverse every tier, so it gets one partition per direction.
    // `file` is only used by the static-asset path (non-admin roles); admin
    // samples are generated server-side from live inventory.
    admin: [
      { label: 'Distributor → Admin', action: 'Admin', file: 'Distributor to Admin_undo_sample.xlsx' },
      { label: 'Dealer → Distributor', action: 'Distributor', file: 'Dealer to Distributor_undo_sample.xlsx' },
      { label: 'Agent → Distributor', action: 'Distributor', file: 'Agent to Distributor_undo_sample.xlsx' },
      { label: 'Agent → Dealer', action: 'Dealer', file: 'Agent to Dealer_undo_sample.xlsx' },
    ],
    distributor: [
      { label: 'Dealer → Distributor', action: 'Distributor', file: 'Dealer to Distributor_undo_sample.xlsx' },
      { label: 'Agent → Distributor', action: 'Distributor', file: 'Agent to Distributor_undo_sample.xlsx' },
    ],
    dealer: [{ label: 'Agent → Dealer', action: 'Dealer', file: 'Agent to Dealer_undo_sample.xlsx' }],
  };
  // Admin sees only the samples for the tier tab it is on — Distributor tab →
  // Distributor → Admin, Dealer tab → Dealer → Distributor, Agent tab → both
  // Agent reversals. Other roles keep their full list.
  const ADMIN_SAMPLES_BY_TIER = {
    distributor: ['Distributor → Admin'],
    dealer: ['Dealer → Distributor'],
    agent: ['Agent → Distributor', 'Agent → Dealer'],
  };
  const samples = isAdmin
    ? sampleFilesByRole.admin.filter((s) => (ADMIN_SAMPLES_BY_TIER[tier] || []).includes(s.label))
    : (sampleFilesByRole[role] || sampleFilesByRole.admin);

  // Fetch the reversible list when the manual tab is active. A request counter
  // discards out-of-order responses, so a slow earlier fetch can't overwrite a
  // newer one and leave the table showing a stale (or empty) result.
  const fetchReversible = async () => {
    const reqId = ++requestIdRef.current;
    setListLoading(true);
    try {
      const res = await API.GetReversibleInventory({
        page, limit, search, childName,
        ...(isAdmin ? {
          tier,
          distributorId: pickedDistributor || '',
          dealerId: tier === 'distributor' ? '' : dealerIdsParam,
          agentId: tier === 'agent' ? agentIdsParam : '',
        } : {}),
      });
      if (reqId !== requestIdRef.current) return;
      if (res?.status === 200 && res?.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotal(res.pagination?.total || 0);
        setNotFoundTags(Array.isArray(res.notFound) ? res.notFound : []);
      } else {
        setRows([]); setTotal(0); setNotFoundTags([]);
        if (res?.message) ErrorToast(res.message);
      }
    } catch (e) {
      if (reqId !== requestIdRef.current) return;
      setRows([]); setTotal(0); setNotFoundTags([]);
      ErrorToast('Failed to load reversible inventory');
    } finally {
      if (reqId === requestIdRef.current) setListLoading(false);
    }
  };

  useEffect(() => {
    if (!open || activeTab !== 'manual') return;
    if (!tierReady) { setRows([]); setTotal(0); return; }
    fetchReversible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab, page, limit, search, childName, tier, pickedDistributor, dealerIdsParam, agentIdsParam, tierReady]);

  // Distributor list for the tier pickers — loaded once per open, admin only.
  useEffect(() => {
    if (!open || !isAdmin) return;
    API.GetDistributorNames()
      .then((res) => setDistributors(asList(res)))
      .catch(() => setDistributors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAdmin]);

  // When the result set shrinks (a search, or a larger page size), the current
  // page can fall past the end and the server returns nothing. Pull it back.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Reset everything each time the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setActiveTab('manual');
      setUndoFile(null);
      setSelected({});
      setPage(1);
      setLimit(10);
      setSearch('');
      setSearchInput('');
      setChildName('');
      setChildInput('');
      setTier('distributor');
      setPickedDistributor('');
      setPickedDealers([]);
      setPickedAgents([]);
      setDealers([]);
      setAgents([]);
    }
  }, [open]);

  if (!open) return null;

  const close = () => { setUndoFile(null); setSelected({}); onClose(); };

  // Every role downloads a static template from /samples. Admin has one per
  // reversal direction; other roles have the ones their role allows.
  const handleDownloadSample = (sample) => {
    try {
      const a = document.createElement('a');
      a.href = `/samples/${encodeURIComponent(sample.file)}`;
      a.download = sample.file;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { ErrorToast('Failed to download sample file'); }
  };

  // ── File tab: upload & undo (unchanged behavior) ──
  const handleUndoUpload = async () => {
    if (!undoFile) { ErrorToast('Please select a file first'); return; }
    setConfirmState(null);
    setIsUndoing(true);
    const formData = new FormData();
    formData.append('file', undoFile);
    try {
      // Admin uses the multi-tier endpoint; every other role keeps the
      // original single-tier one, whose behaviour is unchanged.
      const response = isAdmin
        ? await API.AdminUndoInventoryExcel(formData)
        : await API.UndoInventoryExcel(formData);
      if (response.success) {
        SuccessToast(response.message || 'Undo process completed successfully');
        setUndoFile(null);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        const displayError = typeof response.error === 'string'
          ? response.error
          : (response.error?.message || response.message || 'Failed to process undo');
        ErrorToast(displayError);
      }
    } catch (e) {
      ErrorToast('An error occurred during file upload');
    } finally {
      setIsUndoing(false);
    }
  };

  const askUndoUpload = () => {
    if (!undoFile) { ErrorToast('Please select a file first'); return; }
    setConfirmState({
      title: 'Reverse with file',
      message: `Upload "${undoFile.name}" and reverse the matching FASTags?`,
      onYes: handleUndoUpload,
    });
  };

  // ── Tier walk: each pick narrows the one below it ──
  const changeTier = (next) => {
    setTier(next);
    setPage(1);
    setSelected({});          // a selection from one tier means nothing in another
    setPickedDistributor('');
    setPickedDealers([]);
    setPickedAgents([]);
    setDealers([]);
    setAgents([]);
    setChildInput('');       // the name box is gone below the top tier — drop its filter
    setChildName('');
    // File route is available on every tier now, so changing tier no longer forces
    // the manual tab — the current tab (manual or file) is preserved.
  };

  const pickDistributor = async (id) => {
    setPickedDistributor(id);
    setPickedDealers([]);
    setPickedAgents([]);
    setPage(1);
    setDealers([]);
    setAgents([]);
    if (!id) return;
    try {
      const [dl, ag] = await Promise.all([
        API.GetAllDealersNames({ distributorId: id }),
        // Agent tier with only a distributor chosen: every agent under it,
        // whether they hang off the distributor directly or off one of its dealers.
        tier === 'agent' ? API.GetAgentsNames({ distributorId: id }) : Promise.resolve(null),
      ]);
      setDealers(asList(dl));
      if (ag) setAgents(asList(ag));
    } catch (e) { /* leave the lists empty */ }
  };

  const pickDealer = async (ids) => {
    const list = Array.isArray(ids) ? ids : (ids ? [ids] : []);
    setPickedDealers(list);
    setPickedAgents([]);
    setPage(1);
    if (tier !== 'agent') return;
    try {
      // Narrowing to one or more dealers: the union of their agents, de-duped.
      // Clearing the picker goes back to every agent under the distributor.
      if (list.length === 0) {
        const ag = await API.GetAgentsNames({ distributorId: pickedDistributor || '', dealerId: '' });
        setAgents(asList(ag));
        return;
      }
      const responses = await Promise.all(
        list.map((dealerId) => API.GetAgentsNames({ distributorId: pickedDistributor || '', dealerId }))
      );
      const seen = new Set();
      const merged = [];
      responses.forEach((r) => asList(r).forEach((a) => {
        const id = String(a?._id || a?.id || '');
        if (!id || seen.has(id)) return;
        seen.add(id);
        merged.push(a);
      }));
      setAgents(merged);
    } catch (e) { setAgents([]); }
  };

  // ── Manual tab: selection + undo ──
  const toggleRow = (row) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row._id]) delete next[row._id];
      else next[row._id] = row;
      return next;
    });
  };
  const pageAllSelected = rows.length > 0 && rows.every((r) => selected[r._id]);
  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (pageAllSelected) rows.forEach((r) => delete next[r._id]);
      else rows.forEach((r) => { next[r._id] = r; });
      return next;
    });
  };

  const selectedIds = Object.keys(selected);

  const handleManualUndo = async () => {
    if (selectedIds.length === 0) { ErrorToast('Please select at least one FASTag'); return; }
    setConfirmState(null);
    setSubmitting(true);
    try {
      const res = await API.UndoManualInventory({ inventoryIds: selectedIds });
      if (res?.status === 200 && res?.success) {
        SuccessToast(res.message || 'Reverse completed successfully');
        setSelected({});
        if (onSuccess) onSuccess();
        close();
      } else {
        const errs = res?.data?.errors;
        const msg = Array.isArray(errs) && errs.length
          ? `${errs.slice(0, 3).join(' | ')}${errs.length > 3 ? ` …+${errs.length - 3} more` : ''}`
          : (res?.message || 'Failed to reverse selected FASTags');
        ErrorToast(msg);
      }
    } catch (e) {
      ErrorToast('An error occurred while reversing');
    } finally {
      setSubmitting(false);
    }
  };

  const askManualUndo = () => {
    if (selectedIds.length === 0) { ErrorToast('Please select at least one FASTag'); return; }
    setConfirmState({
      title: 'Reverse inventory',
      message: `Reverse ${selectedIds.length} selected FASTag(s)?`,
      onYes: handleManualUndo,
    });
  };

  // The search box accepts many tag ref ids at once — comma, space or newline
  // separated, so a column pasted straight from Excel works.
  const searchTerms = searchInput.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);

  // Search submits both boxes together; either one may be empty. Checked rows
  // are intentionally NOT cleared here — a selection is meant to survive
  // across searches and pages, so a user can narrow/widen the list repeatedly
  // while building up a batch to undo, then submit it all via Undo Selected.
  const submitSearch = () => {
    setPage(1);
    setSearch(searchTerms.join(' '));
    setChildName(childInput.trim());
  };

  // Clearing the boxes and re-running brings the whole list back. Selection is
  // left untouched, same as submitSearch.
  const clearSearch = () => {
    setPage(1);
    setSearchInput(''); setSearch('');
    setChildInput(''); setChildName('');
  };

  const hasSearch = !!(searchInput || childInput || search || childName);

  // Emptying a box restores the full list immediately — without this the last
  // search stays applied and the table looks stuck on a stale/empty result.
  const onSearchInputChange = (value) => {
    setSearchInput(value);
    if (!value.trim() && search) { setPage(1); setSearch(''); }
  };

  const onChildInputChange = (value) => {
    setChildInput(value);
    if (!value.trim() && childName) { setPage(1); setChildName(''); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-6xl h-[92vh] mx-4 overflow-hidden flex flex-col">

        {/* Header — title on the left, the file route and close on the right */}
        <div className="flex items-start justify-between gap-4 px-6 py-3.5 border-b border-gray-100 shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-1.5 h-8 rounded-full bg-[#EE3B31] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Reverse Inventory</h2>
              <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                {activeTab === 'manual'
                  ? (isAdmin && tier !== 'distributor'
                    ? <>Reversing <b className="text-gray-700">{tier === 'dealer' ? 'dealer' : 'agent'}</b>-held FASTags — pick one or more {tier === 'dealer' ? 'dealers' : 'agents'} below.</>
                    : <>Select non-activated FASTags to return them to the <b className="text-gray-700">{REVERSE_LABEL[role] || 'parent'}</b> tier.</>)
                  : <>Upload an Excel with a <b className="text-gray-700">Reverse_Action</b> column to reverse in bulk.</>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {activeTab === 'manual' ? (fileRouteAvailable && (
              <button
                onClick={() => setActiveTab('file')}
                className="h-[38px] px-4 flex items-center gap-2 rounded-lg border border-[#EE3B31] text-[#EE3B31] text-sm font-semibold transition-all duration-200 hover:bg-[#EE3B31] hover:text-white"
              >
                <Upload size={16} />
                <span className="hidden sm:inline">Reverse with File</span>
              </button>
            )) : (
              <button
                onClick={() => setActiveTab('manual')}
                className="h-[38px] px-4 flex items-center gap-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold transition-colors hover:bg-gray-50 hover:text-gray-800"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Back to list</span>
              </button>
            )}
            <button
              onClick={close}
              className="p-2 text-gray-400 hover:text-[#EE3B31] hover:bg-red-50 rounded-full transition-all duration-200"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ───────────── FILE TAB ───────────── */}
        {activeTab === 'file' && (
          <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col">
            {/* Admin picks the tier here too — the instructions and samples below
                follow it. Same pills (and same tier state) as the manual list. */}
            {isAdmin && (
              <div className="flex items-center gap-2 shrink-0">
                {[
                  { id: 'distributor', label: 'Distributor' },
                  { id: 'dealer', label: 'Dealer' },
                  { id: 'agent', label: 'Agent' },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => changeTier(t.id)}
                    className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${tier === t.id ? 'bg-[#EE3B31] text-white' : 'bg-[#FEF5F5] text-[#1F1715] hover:bg-red-100'
                      }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col md:flex-row justify-center gap-8 py-4">
                <div className="flex flex-col items-center space-y-4 w-full md:w-auto">
                  <div
                    onClick={!isUndoing ? () => fileInputRef.current.click() : undefined}
                    className={`border-2 ${undoFile ? 'border-blue-500' : 'border-red-500'} flex justify-center items-center gap-[18px] rounded-lg p-6 text-center ${undoFile ? 'text-blue-700' : 'text-red-700'} ${!isUndoing ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} text-sm w-full md:w-[306px]`}
                  >
                    {undoFile ? (
                      <div className="flex flex-col items-center">
                        <span className="font-medium mb-1">Selected File:</span>
                        <span className="text-xs text-gray-600 break-all">{undoFile.name}</span>
                        <span className="text-xs text-gray-500">({(undoFile.size / 1024).toFixed(2)} KB)</span>
                      </div>
                    ) : (
                      <>
                        Select / Drop File here
                        <span className="ml-2 text-lg"><img src={uploading} className="w-[18px]" alt="Upload Icon" /></span>
                      </>
                    )}
                  </div>
                  <input type="file" ref={fileInputRef} className="hidden" onChange={(e) => setUndoFile(e.target.files[0])} accept=".xlsx,.xls" />
                  {undoFile && !isUndoing && (
                    <div className="flex gap-2">
                      <button onClick={askUndoUpload} className="px-6 py-2 rounded-full text-sm font-medium bg-red-600 hover:bg-red-700 text-white">Upload & Undo</button>
                      <button onClick={() => { setUndoFile(null); fileInputRef.current.value = ''; }} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-200 hover:bg-gray-300 text-gray-700">Clear</button>
                    </div>
                  )}
                  {isUndoing && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      Processing undo...
                    </div>
                  )}
                </div>

                <div className="hidden md:block w-px bg-gray-200" />

                <div className="text-sm text-gray-700 flex flex-col justify-center">
                  <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wider">Instructions:</p>
                  <ul className="list-disc pl-4 space-y-1 text-gray-600 text-xs text-start">
                    <li>Required columns: Tag Ref ID, Batch ID, Tag Class, <b>Reverse_Action</b></li>
                    <li>Set <b>Reverse_Action</b> to the destination tier — reversals allowed for your role:
                      <ul className="list-[circle] pl-4 mt-1 space-y-0.5">
                        {samples.map((s) => (
                          <li key={s.label}>{s.label} <span className="text-gray-400">(Reverse_Action = <b>{s.action}</b>)</span></li>
                        ))}
                      </ul>
                    </li>
                    <li>
                      {isAdmin
                        ? <>You can reverse <b>any</b> tier — each tag goes back one step to its parent</>
                        : <>You can reverse only your <b>direct</b> child's assignment — not a deeper tier</>}
                    </li>
                    <li><b>Cannot reverse activated tags</b> — only non-activated FASTags can be undone</li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-[10px] text-gray-400">Download the sample for your reversal:</span>
                    <div className="mt-2 flex flex-col gap-1.5">
                      {samples.map((s) => (
                        <button key={s.label} onClick={() => handleDownloadSample(s)} className="text-xs font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 transition-all bg-transparent border-none p-0 cursor-pointer text-left">
                          <Download size={14} />{s.label} sample
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ───────────── MANUAL TAB ───────────── */}
        {activeTab === 'manual' && (
          <div className="flex-1 min-h-0 px-6 pt-4 pb-3 flex flex-col">
            {/* Tier walk — admin only. Distributor is the default and behaves as
                before; Dealer and Agent step further down the tree. */}
            {isAdmin && (
              <div className="mb-3 shrink-0">
                <div className="flex items-center gap-2">
                  {[
                    { id: 'distributor', label: 'Distributor' },
                    { id: 'dealer', label: 'Dealer' },
                    { id: 'agent', label: 'Agent' },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => changeTier(t.id)}
                      className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${tier === t.id ? 'bg-[#EE3B31] text-white' : 'bg-[#FEF5F5] text-[#1F1715] hover:bg-red-100'
                        }`}
                    >
                      {t.label}
                    </button>
                  ))}

                  {/* Below the top tier: say what the list is currently showing,
                      rather than echoing the picked names back as chips. */}
                  {tier !== 'distributor' && (() => {
                    const nameOf = (list, id) => {
                      const u = list.find((x) => (x?._id || x?.id) === id);
                      return u?.username || u?.companyName || u?.name || '';
                    };
                    const distName = pickedDistributor ? nameOf(distributors, pickedDistributor) : '';
                    const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;

                    let message;
                    if (tier === 'dealer') {
                      if (pickedDealers.length) {
                        message = pickedDealers.length === 1
                          ? `Showing stock held by ${nameOf(dealers, pickedDealers[0]) || 'the selected dealer'}`
                          : `Showing stock held by ${plural(pickedDealers.length, 'dealer')}`;
                      } else if (distName) {
                        message = `Showing stock held by every dealer under ${distName}`;
                      } else {
                        message = 'Showing stock held by every dealer';
                      }
                    } else if (pickedAgents.length) {
                      message = pickedAgents.length === 1
                        ? `Showing stock held by ${nameOf(agents, pickedAgents[0]) || 'the selected agent'}`
                        : `Showing stock held by ${plural(pickedAgents.length, 'agent')}`;
                    } else if (pickedDealers.length) {
                      message = pickedDealers.length === 1
                        ? `Showing stock held by agents of ${nameOf(dealers, pickedDealers[0]) || 'the selected dealer'}`
                        : `Showing stock held by agents of ${plural(pickedDealers.length, 'dealer')}`;
                    } else if (distName) {
                      message = `Showing stock held by every agent under ${distName}`;
                    } else {
                      message = 'Choose a distributor to list agent-held stock';
                    }

                    return (
                      <span
                        title={message}
                        className="ml-auto max-w-[45%] truncate text-xs font-semibold text-green-600"
                      >
                        {message}
                      </span>
                    );
                  })()}
                </div>

                {tier !== 'distributor' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2.5">
                    <CustomSelectInput
                      name="tierDistributor"
                      value={pickedDistributor}
                      onChange={(e) => pickDistributor(e.target.value)}
                      options={[
                        { name: 'Select Distributor', value: '' },
                        ...distributors.map((d, i) => ({
                          name: d?.username || d?.companyName || d?.name || `Distributor ${i + 1}`,
                          value: d?._id || d?.id || '',
                        })),
                      ]}
                      showSearch={true}
                      inputClass={`h-[38px] px-3 text-xs bg-white border-gray-200 rounded-lg truncate${tier === 'agent' && pickedDistributor ? ' !border-[#EE3B31] ring-2 ring-[#EE3B31]/20' : ''
                        }`}
                    />
                    {/* Multi-select only on the Dealer tab, where several dealers'
                        stock is reversed together. On the Agent tab the dealer box
                        just narrows which agents are listed, so it takes one dealer. */}
                    <CustomSelectInput
                      name="tierDealer"
                      multiple={tier === 'dealer'}
                      value={tier === 'dealer' ? pickedDealers : (pickedDealers[0] || '')}
                      onChange={(e) => pickDealer(e.target.value)}
                      placeholder={tier === 'agent' ? 'All Dealers' : 'Select Dealer'}
                      options={[
                        { name: tier === 'agent' ? 'All Dealers' : 'Select Dealer', value: '' },
                        ...dealers.map((d, i) => ({
                          name: d?.username || d?.companyName || d?.name || `Dealer ${i + 1}`,
                          value: d?._id || d?.id || '',
                        })),
                      ]}
                      showSearch={true}
                      inputClass={`h-[38px] px-3 text-xs bg-white border-gray-200 rounded-lg truncate${tier === 'agent' && pickedDealers.length > 0 ? ' !border-[#EE3B31] ring-2 ring-[#EE3B31]/20' : ''
                        }`}
                    />
                    {tier === 'agent' && (
                      <CustomSelectInput
                        name="tierAgent"
                        multiple
                        value={pickedAgents}
                        onChange={(e) => {
                          setPickedAgents(Array.isArray(e.target.value) ? e.target.value : []);
                          setPage(1);
                        }}
                        placeholder="Select Agent"
                        options={[
                          { name: 'Select Agent', value: '' },
                          ...agents.map((a, i) => ({
                            name: a?.username || a?.name || `Agent ${i + 1}`,
                            value: a?._id || a?.id || '',
                          })),
                        ]}
                        showSearch={true}
                        inputClass={`h-[38px] px-3 text-xs bg-white border-gray-200 rounded-lg truncate${tier === 'agent' && pickedAgents.length > 0 ? ' !border-[#EE3B31] ring-2 ring-[#EE3B31]/20' : ''
                          }`}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Search — accepts one or many Tag Ref IDs */}
            <div className="mb-3 shrink-0">
              <div className="flex items-start gap-2">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-[13px] text-gray-400" />
                  <textarea
                    value={searchInput}
                    onChange={(e) => onSearchInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter searches; Shift+Enter adds a new id on the next line.
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitSearch(); }
                    }}
                    rows={searchTerms.length > 1 ? 3 : 1}
                    placeholder="Search Tag Ref ID — paste multiple, separated by comma, space or new line"
                    className="w-full bg-white border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm resize-y transition-colors focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20"
                  />
                </div>
                {childSearchAvailable && (
                  <div className="relative w-[220px] shrink-0">
                    <Search size={16} className="absolute left-3 top-[13px] text-gray-400" />
                    <input
                      value={childInput}
                      onChange={(e) => onChildInputChange(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
                      placeholder={CHILD_SEARCH_LABEL[role] || CHILD_SEARCH_LABEL.admin}
                      className="w-full bg-white border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm transition-colors focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20"
                    />
                  </div>
                )}
                <button
                  onClick={submitSearch}
                  className="h-[42px] px-4 text-sm font-semibold rounded-lg bg-[#EE3B31] text-white transition-colors hover:bg-[#d43229]"
                >
                  Search
                </button>
                <button
                  onClick={clearSearch}
                  disabled={!hasSearch}
                  className="h-[42px] px-4 text-sm font-semibold rounded-lg bg-white border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Clear
                </button>
              </div>
              {/* Admin: searched Tag Ref IDs not in this list, with where each is now */}
              {isAdmin && search && notFoundTags.length > 0 && (
                <div className="mt-2 max-h-[110px] overflow-y-auto rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                  <p className="font-semibold mb-1">
                    {notFoundTags.length} Tag Ref ID{notFoundTags.length > 1 ? 's' : ''} not found in this list:
                  </p>
                  <ul className="space-y-0.5">
                    {notFoundTags.map((t) => (
                      <li key={t.tagRefId}>
                        <span className="font-semibold">{t.tagRefId}</span> — {t.currentStatus}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Table — grows to fill the panel's height instead of a fixed crop */}
            <div className="border border-gray-100 rounded-xl overflow-hidden flex-1 min-h-0 flex flex-col shadow-sm">
              <div className="flex-1 min-h-0 overflow-y-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#FAFAFB] border-b border-gray-100">
                      <th className="px-4 py-3.5 w-10">
                        <input
                          type="checkbox"
                          checked={pageAllSelected}
                          onChange={toggleSelectAllOnPage}
                          className="w-4 h-4 rounded border-gray-300 accent-[#EE3B31] cursor-pointer"
                        />
                      </th>
                      <th className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-500">Tag Ref ID</th>
                      <th className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-500">Batch ID</th>
                      <th className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-500">Vehicle Class</th>
                      <th className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-500">Current Holder</th>
                      <th className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-500">Reverse To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-200 border-t-[#EE3B31]" />
                            <p className="mt-3 text-sm font-medium">Loading reversible FASTags…</p>
                          </div>
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="flex flex-col items-center justify-center py-16 text-gray-300">
                            <Inbox className="h-10 w-10" strokeWidth={1.5} />
                            <p className="mt-3 text-sm font-medium text-gray-400">
                              {!tierReady
                                ? (tier === 'dealer'
                                  ? 'Choose a distributor, then one or more dealers to see their inventory'
                                  : 'Choose a distributor to see its agents\' inventory — narrow by dealer or agent')
                                : 'No reversible FASTags found'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : rows.map((r) => {
                      const isSelected = !!selected[r._id];
                      return (
                        <tr
                          key={r._id}
                          onClick={() => toggleRow(r)}
                          className={`border-b border-gray-50 last:border-0 cursor-pointer transition-colors ${isSelected ? 'bg-red-50/60' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRow(r)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-gray-300 accent-[#EE3B31] cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-900 break-all">{r.tagRefId}</td>
                          <td className="px-4 py-3 text-gray-600 break-all">{r.batchId || 'N/A'}</td>
                          <td className="px-4 py-3 text-gray-600">{r.vehicleClass || 'N/A'}</td>
                          <td className="px-4 py-3 text-gray-700">
                            <span className="block leading-tight">{r.currentHolder}</span>
                            <span className="block text-[11px] text-gray-400 capitalize">{r.holderRole}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">{r.reverseTo}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

        {/* Footer — the list's pager lives here too, so the panel is not split
            between two separate bottom bars. */}
        <div className="px-6 py-2.5 bg-[#FAFAFB] border-t border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2 shrink-0">
          {activeTab === 'manual' ? (
            <>
              <span className="text-sm text-gray-500">
                <b className="text-gray-800">{total}</b> reversible tag(s)
                {searchTerms.length > 1 && <> · searching <b className="text-gray-800">{searchTerms.length}</b> ids</>}
                {selectedIds.length > 0 && <> · <b className="text-gray-800">{selectedIds.length}</b> selected</>}
              </span>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1 || listLoading}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-[#EE3B31] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-600 px-1 whitespace-nowrap">
                  <span className="font-semibold text-gray-900">
                    {total === 0 ? 0 : (page - 1) * limit + 1}-{Math.min(page * limit, total)}
                  </span>
                  <span className="text-gray-400"> of {total || 0}</span>
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page >= totalPages || listLoading}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-[#EE3B31] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-2 pl-3 ml-2 border-l border-gray-200">
                  <span className="text-sm text-gray-500">Show</span>
                  <select
                    className="py-1.5 px-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#EE3B31]/20 cursor-pointer"
                    value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  >
                    {pageSizeOptions(total, limit).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : <span />}
          <div className="flex items-center gap-3">
            {activeTab === 'manual' && (
              <button
                onClick={askManualUndo}
                disabled={submitting || selectedIds.length === 0}
                className="h-[38px] px-5 rounded-lg bg-[#EE3B31] text-white text-sm font-semibold shadow-sm transition-all duration-200 hover:bg-[#d43229] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Reversing…' : `Undo Selected (${selectedIds.length})`}
              </button>
            )}
            <button
              onClick={close}
              disabled={isUndoing || submitting}
              className="h-[38px] px-5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-white hover:text-gray-800 transition-colors disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title || 'Please confirm'}
        message={confirmState?.message}
        confirmLabel="Yes, reverse"
        cancelLabel="Cancel"
        loading={submitting || isUndoing}
        onConfirm={() => confirmState?.onYes && confirmState.onYes()}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

const REVERSE_LABEL = { admin: 'Admin', distributor: 'Distributor', dealer: 'Dealer' };

// The second search box targets the tier(s) directly below the logged-in user.
// Accepts multiple names too — comma / space / newline separated, same as
// the Tag Ref ID box.
const CHILD_SEARCH_LABEL = {
  admin: 'Search Distributor name(s)',
  distributor: 'Search Dealer / Agent name(s)',
  dealer: 'Search Agent name(s)',
};

export default ReverseInventoryModal;
